import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import RemboLogo from "./components/RemboLogo";
import "./css/landing_page.css";

// ─── Data ─────────────────────────────────────────────────────────────────────
const marqueeItems = [
  "Hair Styling", "·", "Nail Technology", "·", "Skincare & Aesthetics", "·",
  "Makeup Artistry", "·", "Barbering", "·", "Spa & Wellness", "·",
  "Hospitality", "·", "Beauty Education", "·", "Salon Management", "·",
  "Hair Styling", "·", "Nail Technology", "·", "Skincare & Aesthetics", "·",
  "Makeup Artistry", "·", "Barbering", "·", "Spa & Wellness", "·",
  "Hospitality", "·", "Beauty Education", "·", "Salon Management", "·",
];

const steps = [
  {
    n: "01",
    title: "Set up your profile",
    text: "Employers post their business and open roles. Professionals build a profile that shows off their skills and specialisations — not just a CV.",
  },
  {
    n: "02",
    title: "Find what fits",
    text: "Browse roles tailored to the beauty and hospitality world, or let relevant listings come to you. Filter by location, discipline, and availability.",
  },
  {
    n: "03",
    title: "Make the connection",
    text: "Apply, shortlist, and communicate — all in one place. From first contact to confirmed hire, we keep the process simple and human.",
  },
];

const employers = [
  "Salons and barbershops",
  "Spas and wellness centres",
  "Aesthetic and skincare clinics",
  "Beauty brands and distributors",
  "Beauty shops and cosmetics retailers",
  "Restaurants, hotels and resorts",
  "Training institutions",
];

const jobSeekers = [
  "Beauty therapists and aestheticians",
  "Nail technicians and barbers",
  "Makeup artists",
  "Beauty advisors and sales staff",
  "Receptionists and front-of-house",
  "Trainers and educators",
  "Hospitality professionals",
  "Students and recent graduates",
];

const testimonials = [
  {
    text: "Within two weeks of posting, I had three qualified stylists apply. Rembolist understands our industry in a way generic job boards simply don't.",
    name: "Amara Osei",
    role: "Salon owner, Nairobi",
    initials: "AO",
  },
  {
    text: "I'd been freelancing for years but wanted something steadier. Rembolist matched me with a spa that fit my specialty perfectly. Life-changing.",
    name: "Fatima Al-Rashidi",
    role: "Esthetician",
    initials: "FA",
  },
  {
    text: "Every applicant actually understood the industry. We hired our head nail technician within a month — something I couldn't manage on other platforms.",
    name: "Chloe Mwangi",
    role: "Spa director",
    initials: "CM",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* FIXED NAV */}
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <Link to="/" className="nav-logo">
          <RemboLogo theme="light" />
        </Link>
        <div className="nav-links">
          <a href="#about">About</a>
          <a href="#how">How It Works</a>
          <a
            href="#"
            className="nav-cta"
            onClick={(e) => {
              e.preventDefault();
              navigate("/signin");
            }}
          >
            Sign In
          </a>
        </div>
      </nav>

      {/* ALL LAYOUT WRAPPED IN CONTROLLED CONTENT BLOCK */}
      <main className="main-content">

        {/* THE MARQUEE STRIP */}
        <div className="strip">
          <div className="strip-inner">
            {marqueeItems.map((item, i) => (
              <span key={i} className={item === "·" ? "dot" : ""}>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* HERO */}
        <section className="hero">
          <div className="hero-left">
            <div className="tag">Beauty · Wellness · Hospitality</div>
            <h1 className="hero-title">
              Where the right people
              <br />
              find the right <em>places</em>
            </h1>
            <p className="hero-body">
              Rembolist is a dedicated job board for the beauty, wellness, and
              hospitality industries — built to make hiring less painful and job
              searching far more targeted. No more irrelevant listings.
            </p>
            <div className="hero-actions">
              <button
                className="btn-dark"
                onClick={() => navigate("/dashboard")}
              >
                Find Work
              </button>
              <button
                className="btn-line"
                onClick={() => navigate("/employer/dashboard")}
              >
                Post a Job
              </button>
            </div>
          </div>

          <div className="hero-right">
            <div className="hero-right-label">Who we connect</div>
            <div className="serve-group">
              <div className="serve-group-title">Employers</div>
              <div className="serve-items">
                {employers.map((item) => (
                  <div key={item} className="serve-item">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <hr className="serve-divider" />
            <div className="serve-group">
              <div className="serve-group-title">Professionals</div>
              <div className="serve-items">
                {jobSeekers.slice(0, 5).map((item) => (
                  <div key={item} className="serve-item">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section className="about-section" id="about">
          <div>
            <div className="section-tag">About Rembolist</div>
            <h2 className="section-title">
              A job board built for <em>this</em> industry
            </h2>
            <p className="section-body">
              Rembolist is a specialised platform for the beauty, wellness, and
              hospitality sectors. Generic job boards are built for everyone —
              which means they're rarely built for you. We exist to fix that gap:
              giving businesses a faster way to find qualified professionals, and
              giving job seekers a space where their skills are actually
              understood.
              <br />
              <br />
              Whether you're a salon owner tired of CVs that don't fit, or a
              beauty therapist looking for something more than a broad search
              engine, Rembolist was made with you in mind.
            </p>
          </div>
          <div>
            <div className="mission-block">
              <p className="mission-quote">
                Our mission is simple: to make hiring easier and help talented
                professionals connect with the{" "}
                <strong>right opportunities</strong> — not just any opportunities.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="how-section" id="how">
          <div className="section-tag">How It Works</div>
          <h2 className="section-title">
            Straightforward, <em>by design</em>
          </h2>
          <div className="how-grid">
            {steps.map((s) => (
              <div key={s.n} className="step-card">
                <div className="step-num" aria-hidden="true">{s.n}</div>
                <div className="step-line" />
                <div className="step-title">{s.title}</div>
                <p className="step-text">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* WHO WE SERVE */}
        <section className="serve-section">
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="section-tag">Who We Serve</div>
            <h2
              className="section-title"
              style={{ color: "var(--paper)", maxWidth: "540px" }}
            >
              Two sides of the same <em>industry</em>
            </h2>
            <div className="serve-grid">
              <div className="serve-card">
                <div className="serve-role">For Employers</div>
                <div className="serve-card-title">Businesses &amp; Institutions</div>
                <p className="serve-card-body">
                  Stop sorting through applications that don't match. Post your
                  roles once and reach professionals who actually work in your
                  field — from independent salons to hotel groups.
                </p>
                <ul className="serve-list">
                  {employers.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="serve-card">
                <div className="serve-role">For Job Seekers</div>
                <div className="serve-card-title">
                  Beauty &amp; Hospitality Professionals
                </div>
                <p className="serve-card-body">
                  Find roles that actually match your background — whether you're
                  just starting out, switching specialisations, or looking for
                  something senior.
                </p>
                <ul className="serve-list">
                  {jobSeekers.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="testi-section">
          <div className="section-tag">Success Stories</div>
          <h2 className="section-title">
            Heard from the <em>community</em>
          </h2>
          <div className="testi-grid">
            {testimonials.map((t) => (
              <div key={t.name} className="testi-card">
                <div className="testi-open">"</div>
                <p className="testi-text">{t.text}</p>
                <div className="testi-author">
                  <div className="testi-avatar">{t.initials}</div>
                  <div>
                    <div className="testi-name">{t.name}</div>
                    <div className="testi-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="cta-inner">
            <div
              className="section-tag"
              style={{ justifyContent: "center", display: "flex" }}
            >
              Get started
            </div>
            <h2 className="cta-title">
              Your next step
              <br />
              starts <em>here</em>
            </h2>
            <p className="cta-body">
              Whether you're building a team or building a career, Rembolist is
              the dedicated platform the beauty, wellness, and hospitality
              industries have needed.
            </p>
            <div className="cta-buttons">
              <button
                className="btn-gold"
                onClick={() => navigate("/dashboard")}
              >
                I'm looking for work
              </button>
              <button
                className="btn-ghost"
                onClick={() => navigate("/employer/dashboard")}
              >
                I'm hiring talent
              </button>
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-top">
          <div>
            <div className="footer-logo">
              <RemboLogo theme="dark" />
            </div>
            <p className="footer-tagline">
              The dedicated job board for beauty, wellness, and hospitality
              across East Africa.
            </p>
          </div>
          <div>
            <div className="footer-col-title">Platform</div>
            <ul className="footer-links">
              <li><Link to="/dashboard">Browse Jobs</Link></li>
              <li><Link to="/employer/dashboard">Post a Job</Link></li>
              <li><a href="#about">About Us</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">© 2026 Rembolist. All rights reserved.</div>
          <div className="footer-legal"></div>
        </div>
      </footer>
    </>
  );
}